import React, { useMemo, useRef, useState } from 'react';
import {
  createAssetProcessingDoc,
  downloadBlob,
  exportDomToPdf,
  exportDomToWord,
  failAssetDoc,
  finalizeAssetDoc,
  uploadBlobToStorage,
} from './printablesExport';

const PAGE_SIZE_MM = { width: 210, height: 297 };
const BUSINESS_CARD_LOGO_URL = '/d_logo-removebg-preview.png';

const renderLogo = (logoUrl, fallbackText) => {
  if (logoUrl) {
    return <img src={logoUrl} alt="Douglas & McCartney logo" crossOrigin="anonymous" />;
  }
  const fallback = String(fallbackText || '').trim() || 'Douglas & McCartney';
  const [first, ...rest] = fallback.split('&');
  const remainder = rest.join('&').trim();
  return (
    <div className="printable-logo-text">
      <span>{first.trim()} &amp;</span>
      <span>{remainder || 'McCartney'}</span>
    </div>
  );
};

const buildMissing = ({ compliance, profile, branch }) => {
  const missing = [];
  if (compliance.requirePhone && !profile.phone) missing.push('phone');
  if (compliance.requireAddress && !branch.address) missing.push('address');
  if (compliance.requireRera && !branch.reraId) missing.push('rera');
  return missing;
};

const LetterheadPrintable = ({
  firebase,
  authUser,
  tenantId,
  branchId,
  profile,
  branch,
  brandKit,
  assignedEmail,
  assignedEmailMissing,
  compliance,
}) => {
  const pageRef = useRef(null);

  const [exportingType, setExportingType] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');

  const missing = useMemo(
    () => buildMissing({ compliance, profile, branch }),
    [branch, compliance, profile],
  );

  const isBlocked = missing.length > 0;
  const statusTone = isBlocked ? 'error' : assignedEmailMissing ? 'warning' : 'success';
  const canSave = Boolean(tenantId && branchId && authUser?.uid);

  const buildAssetSnapshot = () => ({
    fullName: profile.fullName || '',
    designation: profile.designation || '',
    phone: profile.phone || '',
    assignedEmail: assignedEmail || '',
    branchName: branch.branchName || '',
    address: branch.address || '',
    city: branch.city || '',
    reraId: branch.reraId || '',
    brand: {
      colors: brandKit?.colors || {},
      disclaimerText: brandKit?.disclaimerText || '',
      logoUrl: BUSINESS_CARD_LOGO_URL,
    },
    letterhead: {
      templateKey: 'blank',
      bodyText: '',
      placeholders: {},
      styleSettings: {},
      includeRefBlock: false,
      subject: '',
    },
  });

  const uploadPdfToAssets = async (blob) => {
    if (!canSave) {
      setWarning('Upload failed, file not saved.');
      return;
    }

    let assetRef = null;
    try {
      assetRef = await createAssetProcessingDoc({
        firebase,
        tenantId,
        branchId,
        authUserId: authUser?.uid || '',
        type: 'letterhead',
        snapshot: buildAssetSnapshot(),
      });

      const storagePath = `tenants/${tenantId}/assets/${assetRef.id}/letterhead.pdf`;
      const url = await uploadBlobToStorage({
        firebase,
        storagePath,
        blob,
        contentType: 'application/pdf',
        metadata: { tenantId, branchId, createdBy: authUser?.uid || '' },
      });

      await finalizeAssetDoc({
        firebase,
        assetRef,
        output: { pdfUrl: url, storagePath },
      });
    } catch (uploadError) {
      console.error('Letterhead asset upload failed:', uploadError);
      setWarning('Upload failed, file not saved.');
      if (assetRef) {
        await failAssetDoc({ firebase, assetRef, errorMessage: uploadError.message });
      }
    }
  };

  const handlePdfExport = async () => {
    if (isBlocked || !pageRef.current) return;

    setExportingType('pdf');
    setError('');
    setWarning('');
    setMessage('');

    try {
      const fileName = `Douglas-McCartney-Letterhead-${profile.fullName || 'franchisee'}.pdf`;
      const blob = await exportDomToPdf({
        element: pageRef.current,
        fileName,
        pageSizeMm: PAGE_SIZE_MM,
        backgroundColor: '#ffffff',
        marginMm: 8,
      });

      await uploadPdfToAssets(blob);
      setMessage('Letterhead downloaded as PDF.');
    } catch (downloadError) {
      console.error('Letterhead PDF export failed:', downloadError);
      setError('Could not generate PDF. Try again.');
    } finally {
      setExportingType('');
    }
  };

  const handleWordExport = () => {
    if (isBlocked || !pageRef.current) return;

    setExportingType('word');
    setError('');
    setWarning('');
    setMessage('');

    try {
      const blob = exportDomToWord({ element: pageRef.current });
      const fileName = `Douglas-McCartney-Letterhead-${profile.fullName || 'franchisee'}.doc`;
      downloadBlob(blob, fileName);
      setMessage('Letterhead downloaded as Word document.');
    } catch (downloadError) {
      console.error('Letterhead Word export failed:', downloadError);
      setError('Could not generate Word file. Try again.');
    } finally {
      setExportingType('');
    }
  };

  return (
    <div className="printable-layout">
      <div className="printable-preview letterhead-preview">
        <div className="printable-page" ref={pageRef}>
          {brandKit?.watermarkUrl && (
            <div className="printable-watermark">
              <img src={brandKit.watermarkUrl} alt="Watermark" crossOrigin="anonymous" />
            </div>
          )}
          <div className="page-inner">
            <div className="printable-header">
              <div className="printable-logo">
                {renderLogo(BUSINESS_CARD_LOGO_URL, 'Douglas & McCartney')}
              </div>
              <div className="printable-contact">
                <div>{profile.phone || 'Phone needed'}</div>
                <div>{assignedEmail || 'Email pending'}</div>
              </div>
            </div>

            <div
              className="letterhead-body"
              style={{
                fontSize: '12px',
                lineHeight: 1.6,
                textAlign: 'left',
              }}
            >
              {brandKit?.watermarkUrl && (
                <div className="letterhead-body-watermark" style={{ opacity: 0.08 }}>
                  <img src={brandKit.watermarkUrl} alt="Body watermark" crossOrigin="anonymous" />
                </div>
              )}
              <div className="letterhead-body-inner">
                <div className="letterhead-body-content" />
              </div>
            </div>

            <div className="printable-footer">
              <strong>{branch.branchName || 'Douglas & McCartney'}</strong>
              <div>{branch.address || 'Address required'}</div>
              <div>{branch.city || ''}</div>
              {branch.reraId && <div>RERA: {branch.reraId}</div>}
              {brandKit?.disclaimerText && <div>{brandKit.disclaimerText}</div>}
            </div>
          </div>
        </div>
      </div>

      <div className="printable-panel">
        <div className={`printable-status ${statusTone}`}>
          {isBlocked ? 'Missing Requirements' : 'Ready'}
        </div>
        {assignedEmailMissing && (
          <div className="printable-banner warning">Assigned email not set yet.</div>
        )}
        {missing.length > 0 && (
          <div className="printable-banner error">
            Missing: {missing.join(', ')}.
            <button
              type="button"
              className="printable-link"
              onClick={() => window.location.assign('/account')}
            >
              Fix in Profile
            </button>
          </div>
        )}
        {message && <div className="printable-banner info">{message}</div>}
        {warning && <div className="printable-banner warning">{warning}</div>}
        {error && <div className="printable-banner error">{error}</div>}

        <div className="printable-actions">
          <button
            type="button"
            className="printable-action"
            disabled={Boolean(exportingType) || isBlocked}
            onClick={handlePdfExport}
          >
            {exportingType === 'pdf' ? 'Generating PDF...' : 'Download PDF'}
          </button>
          <button
            type="button"
            className="printable-action"
            disabled={Boolean(exportingType) || isBlocked}
            onClick={handleWordExport}
          >
            {exportingType === 'word' ? 'Generating Word...' : 'Download Word'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LetterheadPrintable;
